import { GPM } from '../gpmAPI';

export interface NotificationSettingsQueryProps {
	apiToken: string;
	programID?: string;
}

interface NotificationSettingsMutationProps {
	id: string;
	apiToken: string;
	enabled: boolean;
	programID?: string;
}

export interface SendManualEmailProps {
	apiToken: string;
	to: string;
	title: string;
	content: string;
}

export interface EmailTemplate {
	email_template_name: string;
	email_template_id: string;
	description?: string;
}

export interface OrganizationNotificationRow {
	organization_notification_settings_id: string | number;
	active: boolean;
	email_template?: EmailTemplate;
}

export interface ProgramNotificationRow {
	program_notification_settings_id: string | number;
	active: boolean;
	org_active: boolean;
	email_template?: EmailTemplate;
}

export interface NotificationSettingsResponse<T> {
	results: T[];
	count: number;
}

const notificationsApi = GPM.injectEndpoints({
	endpoints: (builder) => ({
		getOrganizationNotificationsSettings: builder.query<
			NotificationSettingsResponse<OrganizationNotificationRow>,
			NotificationSettingsQueryProps
		>({
			query: (props: NotificationSettingsQueryProps) => ({
				url: 'notifications/organization-notification-settings/',
				method: 'GET',
				headers: {
					Authorization: `Bearer ${props?.apiToken}`
				}
			}),
			providesTags: ['OrganizationNotifications']
		}),
		updateOrganizationNotificationsSettings: builder.mutation({
			query: (props: NotificationSettingsMutationProps) => ({
				url: `notifications/organization-notification-settings/${props.id}/`,
				method: 'PATCH',
				body: {
					enabled: props?.enabled
				},
				headers: {
					Authorization: `Bearer ${props?.apiToken}`
				}
			}),
			invalidatesTags: ['OrganizationNotifications']
		}),
		sendManualEmail: builder.mutation<{ detail: string }, SendManualEmailProps>({
			query: (props: SendManualEmailProps) => ({
				url: 'notifications/organization-notification-settings/send-manual-email/',
				method: 'POST',
				body: {
					to: props.to,
					title: props.title,
					content: props.content
				},
				headers: {
					Authorization: `Bearer ${props?.apiToken}`
				}
			})
		}),
		getProgramNotificationsSettings: builder.query<
			ProgramNotificationRow[],
			NotificationSettingsQueryProps
		>({
			query: (props: NotificationSettingsQueryProps) => ({
				url: `notifications/program-notification-settings/program/${props.programID}/`,
				method: 'GET',
				headers: {
					Authorization: `Bearer ${props?.apiToken}`
				}
			}),
			providesTags: ['ProgramNotifications']
		}),
		updateProgramNotificationsSettings: builder.mutation({
			query: (props: NotificationSettingsMutationProps) => ({
				url: `notifications/program-notification-settings/${props.id}/`,
				method: 'PATCH',
				body: {
					enabled: props?.enabled
				},
				headers: {
					Authorization: `Bearer ${props?.apiToken}`
				}
			}),
			invalidatesTags: ['ProgramNotifications']
		})
	}),
	overrideExisting: false
});

export const {
	useGetOrganizationNotificationsSettingsQuery,
	useUpdateOrganizationNotificationsSettingsMutation,
	useSendManualEmailMutation,
	useGetProgramNotificationsSettingsQuery,
	useUpdateProgramNotificationsSettingsMutation
} = notificationsApi;
